import React, { useEffect, useMemo, useState } from "react";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { Phone, RefreshCw, Save, ShieldAlert, UserRound } from "lucide-react";
import ReactSelect from "../../ReactSelect";
import {
  getHumanInLoopError,
  humanInLoopApi,
} from "../../api/humanInLoopApi";
import "./humanInLoop.css";

const managerId = () =>
  localStorage.getItem("managerMobile") || localStorage.getItem("managerId") || "MANAGER";

const driverLabel = (driver) =>
  `${driver?.driver_name || "Unnamed driver"} · ${driver?.mobile_number || "No phone number"}`;

const ManualAllocationModal = ({ isOpen, booking, onClose, onSaved }) => {
  const bookingId = Number(booking?.booking_id || booking?.id || 0);
  const [drivers, setDrivers] = useState([]);
  const [state, setState] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState("");
  const [assignmentLock, setAssignmentLock] = useState("VEHICLE");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const load = async () => {
      if (!isOpen || !bookingId) return;
      setLoading(true);
      setError("");
      setState(null);
      setDrivers([]);
      setSelectedVehicle("");
      try {
        const [driverResponse, stateResponse] = await Promise.all([
          humanInLoopApi.getDrivers(),
          humanInLoopApi.getBookingState(bookingId),
        ]);
        setDrivers(driverResponse.data || []);
        setState(stateResponse.data);
        setSelectedVehicle(stateResponse.data.assigned_vehicle_id || "");
        setAssignmentLock(stateResponse.data.assignment_lock || "VEHICLE");
      } catch (requestError) {
        setError(getHumanInLoopError(requestError, "Unable to load allocation state."));
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [isOpen, bookingId]);

  const driverOptions = useMemo(
    () => drivers.map((driver) => ({
      value: String(driver.vehicle_id),
      label: driverLabel(driver),
      searchText: `${driver.driver_name || ""} ${driver.mobile_number || ""}`,
      driver,
    })),
    [drivers],
  );
  const selectedOption = useMemo(
    () => driverOptions.find((option) => String(option.value) === String(selectedVehicle)) || null,
    [driverOptions, selectedVehicle],
  );
  const selectedDriver = selectedOption?.driver;

  const handleSave = async () => {
    if (!bookingId || !state) return;
    if (!selectedVehicle) {
      setError("Select a driver before saving.");
      return;
    }

    setSubmitting(true);
    setError("");
    let sessionId = null;
    try {
      const currentVehicleId = state.assigned_vehicle_id
        ? String(state.assigned_vehicle_id)
        : null;
      const targetVehicleId = String(selectedVehicle);
      const isReassign = Boolean(currentVehicleId && currentVehicleId !== targetVehicleId);
      const affectedVehicleIds = [...new Set([
        ...(currentVehicleId ? [currentVehicleId] : []),
        targetVehicleId,
      ])];
      const expectedTourVersions = {};
      for (const vehicleId of affectedVehicleIds) {
        const driver = drivers.find((row) => String(row.vehicle_id) === vehicleId);
        expectedTourVersions[vehicleId] = vehicleId === currentVehicleId
          ? Number(state.tour_version || 0)
          : Number(driver?.tour_version || 0);
      }

      const sessionResponse = await humanInLoopApi.createEditSession({
        manager_id: managerId(),
        manager_name: "Operations Manager",
        vehicle_ids: affectedVehicleIds,
        booking_ids: [String(bookingId)],
        base_tour_versions: expectedTourVersions,
        ttl_sec: 300,
        reason: isReassign ? "MANUAL_REASSIGNMENT" : "MANUAL_ASSIGNMENT",
      });
      sessionId = sessionResponse?.session?.session_id;
      if (!sessionId) throw new Error("The allocator did not return an edit session ID.");

      const payload = {
        edit_session_id: sessionId,
        vehicle_id: targetVehicleId,
        expected_tour_version: Number(expectedTourVersions[targetVehicleId] || 0),
        expected_assignment_version: Number(state.assignment_version || 0),
        assignment_lock: assignmentLock,
        sequence_lock: "NONE",
        manager_id: managerId(),
        reason_code: isReassign ? "MANUAL_REASSIGNMENT_FROM_TRIPS" : "MANUAL_ASSIGNMENT_FROM_TRIPS",
        reason_text: `Assigned booking ${bookingId} from the Trips page`,
      };
      if (isReassign) {
        await humanInLoopApi.reassignBooking(bookingId, {
          ...payload,
          from_vehicle_id: currentVehicleId,
          expected_from_tour_version: Number(expectedTourVersions[currentVehicleId] || 0),
        });
      } else {
        await humanInLoopApi.assignBooking(bookingId, payload);
      }
      sessionId = null;
      onSaved?.();
      onClose?.();
    } catch (requestError) {
      if (sessionId) {
        try { await humanInLoopApi.release(sessionId); } catch (_) {}
      }
      setError(
        requestError?.response?.status === 409
          ? "The booking or selected driver plan changed. Close and reopen this window to use the latest state."
          : getHumanInLoopError(requestError, "Unable to save the assignment."),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg" backdrop="static">
      <ModalHeader toggle={onClose}>
        {state?.assigned_vehicle_id ? "Manage" : "Assign"} booking #{bookingId || ""}
      </ModalHeader>
      <ModalBody>
        {loading && (
          <div className="hitl-centre">
            <RefreshCw className="hitl-spin" /> Loading assignment…
          </div>
        )}
        {error && (
          <div className="hitl-alert">
            <ShieldAlert size={18} /> {error}
          </div>
        )}
        {!loading && state && (
          <div className="hitl-manual-allocation">
            <div className="hitl-current-assignment">
              <span className="hitl-planned-label">Current assignment</span>
              {state.assigned_vehicle_id ? (
                <>
                  <span><UserRound size={16} /> {state.driver_name || "Unnamed driver"}</span>
                  <span><Phone size={16} /> {state.driver_mobile || "No phone number"}</span>
                </>
              ) : (
                <strong className="hitl-unassigned-label">Unassigned — available in Trips</strong>
              )}
            </div>
            <div className="hitl-form-field">
              <label>Driver</label>
              <ReactSelect
                options={driverOptions}
                value={selectedOption}
                handleSelectedOption={(option) => setSelectedVehicle(option?.value || "")}
                placeholder="Search driver name or phone"
                isDisabled={submitting}
              />
            </div>
            <div className="hitl-form-field">
              <label>Assignment protection</label>
              <select
                value={assignmentLock}
                onChange={(event) => setAssignmentLock(event.target.value)}
                disabled={submitting}
              >
                <option value="NONE">No assignment lock</option>
                <option value="VEHICLE">Keep selected driver</option>
                <option value="FULL">Lock driver and sequence</option>
              </select>
            </div>
            {selectedDriver && (
              <div className="hitl-selected-driver-card">
                <span><UserRound size={16} /> {selectedDriver.driver_name || "Unnamed driver"}</span>
                <span><Phone size={16} /> {selectedDriver.mobile_number || "No phone number"}</span>
              </div>
            )}
            <div className="hitl-inline-note">
              Unassignment is managed from the Tours edit/merge planner. This Trips action only assigns or reassigns a booking.
            </div>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <button type="button" className="hitl-secondary-button" onClick={onClose} disabled={submitting}>
          Cancel
        </button>
        <button
          type="button"
          className="hitl-primary-button"
          onClick={handleSave}
          disabled={loading || submitting || !state}
        >
          {submitting ? <RefreshCw className="hitl-spin" size={17} /> : <Save size={17} />}
          {submitting ? "Saving…" : "Save assignment"}
        </button>
      </ModalFooter>
    </Modal>
  );
};

export default ManualAllocationModal;
